import type { UserDirectoryEntry } from "./types";

/**
 * Renders an HR user reference as a single human-friendly string.
 *
 * Rules:
 *   - Resolved entry with a display name → "Jason Gray (USR471)"
 *   - Resolved entry without a display name but with email →
 *     "jason.gray@sagefreight.com (USR471)"
 *   - Resolved entry with neither → just "USR471"
 *   - Unresolved entry (pending lookup, HR 404) → "USR471"
 *
 * The id is always shown alongside the friendly name so admins still
 * have the canonical reference to copy/paste into other tools when
 * troubleshooting an assignment, and so the display is unambiguous
 * across users with the same display name.
 */
export function formatUserDisplay(
    entry: UserDirectoryEntry | null | undefined,
    fallbackId: string,
): string {
    if (entry === null || entry === undefined) {
        return fallbackId;
    }

    const friendly = pickFriendly(entry);
    if (friendly === null) {
        return fallbackId;
    }

    return `${friendly} (${entry.id})`;
}

/**
 * Compact name for dense UI (floorplan desk labels). Prefer display
 * name, then email / UPN — never appends the user id.
 */
export function formatUserShortName(
    entry: UserDirectoryEntry | null | undefined,
): string | null {
    if (entry === null || entry === undefined) return null;
    return pickFriendly(entry);
}

function pickFriendly(entry: UserDirectoryEntry): string | null {
    if (
        entry.displayName !== null &&
        entry.displayName !== undefined &&
        entry.displayName !== ""
    ) {
        return entry.displayName;
    }
    if (entry.email !== null && entry.email !== undefined && entry.email !== "") {
        return entry.email;
    }
    if (
        entry.userPrincipalName !== null &&
        entry.userPrincipalName !== undefined &&
        entry.userPrincipalName !== ""
    ) {
        return entry.userPrincipalName;
    }
    return null;
}
